import {
	OnboardingSeedingStatus,
	type OnboardingStep,
	OnboardingStepStatus,
	Permission,
	TenantConfigPlane,
	type TenantOnboardingDTO,
} from '@today/types'
import {
	ErrorState,
	FormSkeleton,
	ListSkeleton,
	NotificationLevel,
	NotificationStatusBar,
	StepIndicator,
	StepIndicatorStatus,
} from '@today/ui-components'
import { type MouseEvent, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { RequirePermission } from '@/components/RequirePermission'
import { type OnboardingWizardState, useOnboardingWizard } from '@/hooks/useOnboardingWizard'
import { classifyLoadError, classifySaveError } from '@/modules/onboarding/onboarding-errors'
import { isSelectable } from '@/modules/onboarding/wizard-navigation'
import {
	ONBOARDING_WIZARD_REGISTRY,
	type OnboardingWizardRegistry,
	type OnboardingWizardStep,
	WizardStepKind,
} from '@/modules/onboarding/wizard-registry'
import { StepActions } from './StepActions'

interface OnboardingPageProps {
	/** The wizard's steps; the shipped registry by default (tests inject their own). */
	registry?: OnboardingWizardRegistry
}

const INDICATOR_STATUS: Record<OnboardingStepStatus, StepIndicatorStatus> = {
	[OnboardingStepStatus.NOT_STARTED]: StepIndicatorStatus.NOT_STARTED,
	[OnboardingStepStatus.IN_PROGRESS]: StepIndicatorStatus.IN_PROGRESS,
	[OnboardingStepStatus.COMPLETE]: StepIndicatorStatus.COMPLETE,
	[OnboardingStepStatus.SKIPPED]: StepIndicatorStatus.SKIPPED,
}

/**
 * `/t/:slug/onboarding` — the tenant onboarding wizard (TO-190). A shell over
 * the existing setup surfaces: it orders the steps, records each step's status
 * on the server, and embeds a tenant-config section or links out to a page.
 * Where a first-time admin lands after accepting their invitation (TO-309).
 */
export function OnboardingPage({ registry = ONBOARDING_WIZARD_REGISTRY }: OnboardingPageProps) {
	return (
		<RequirePermission permission={Permission.TENANT_MANAGE_SETTINGS}>
			<Wizard registry={registry} />
		</RequirePermission>
	)
}

function Wizard({ registry }: { registry: OnboardingWizardRegistry }) {
	const { slug = '' } = useParams()
	const { isLoading, loadError, retryLoad, state } = useOnboardingWizard(slug, registry)
	const [seedingDismissed, setSeedingDismissed] = useState(false)

	if (isLoading) return <WizardSkeleton />
	if (!state) {
		return (
			<section className="mx-auto max-w-4xl p-8">
				<ErrorState variant={classifyLoadError(loadError)} onRetry={retryLoad} />
			</section>
		)
	}
	const { onboarding, active } = state

	return (
		<section className="mx-auto flex max-w-4xl flex-col gap-6 p-8">
			<h1 className="text-2xl font-bold">Set up {slug}</h1>
			<SeedingBanner onboarding={onboarding} dismissed={seedingDismissed} onDismiss={() => setSeedingDismissed(true)} />
			<div className="flex flex-col gap-6 md:flex-row">
				<div className="md:w-64">
					<StepIndicator
						currentId={active.step}
						onSelect={(id) => state.select(id as OnboardingStep)}
						items={onboarding.steps.map((step) => ({
							id: step.step,
							label: registry[step.step].title,
							status: INDICATOR_STATUS[step.status],
							optional: !step.required,
							selectable: isSelectable(onboarding.steps, step.step),
						}))}
					/>
				</div>
				<StepPanel state={state} slug={slug} />
			</div>
		</section>
	)
}

/** TO-733 REQ-005: the wizard's two-column shape while the read is pending, so nothing shifts. */
function WizardSkeleton() {
	return (
		<section aria-busy="true" className="mx-auto flex max-w-4xl flex-col gap-6 p-8">
			<div className="flex flex-col gap-6 md:flex-row">
				<ListSkeleton rows={5} labels={{ loading: 'Loading steps' }} className="md:w-64" />
				<FormSkeleton labels={{ loading: 'Loading step' }} className="flex-1" />
			</div>
		</section>
	)
}

function StepPanel({ state, slug }: { state: OnboardingWizardState; slug: string }) {
	const { onboarding, entry } = state
	return (
		<div className="flex flex-1 flex-col gap-4">
			<h2 className="text-xl font-semibold">{entry.title}</h2>
			<p className="text-ds-text-muted">{entry.description}</p>
			<StepContent entry={entry} tenantId={onboarding.tenantId} slug={slug} onFollow={state.followLink} />
			{state.saveError !== null && <SaveError error={state.saveError} onRetry={state.retrySave} />}
			<StepActions state={state} />
		</div>
	)
}

/** TO-733 REQ-006: a refusal is explained and not retried; a network/server failure offers Retry. */
function SaveError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
	const failure = classifySaveError(error)
	return (
		<ErrorState variant={failure.variant} message={failure.message} onRetry={failure.retryable ? onRetry : undefined} />
	)
}

function SeedingBanner({
	onboarding,
	dismissed,
	onDismiss,
}: {
	onboarding: TenantOnboardingDTO
	dismissed: boolean
	onDismiss: () => void
}) {
	// REQ-012: only a FAILED seeding summary shows the warning.
	if (dismissed || onboarding.seeding.status !== OnboardingSeedingStatus.FAILED) return null
	return (
		<NotificationStatusBar
			onDismiss={onDismiss}
			items={[
				{
					id: 'seeding',
					level: NotificationLevel.WARNING,
					title: 'Some starter data could not be created',
					description: `Failed: ${onboarding.seeding.failedContexts.join(', ')}. Contact your platform administrator.`,
				},
			]}
		/>
	)
}

function StepContent({
	entry,
	tenantId,
	slug,
	onFollow,
}: {
	entry: OnboardingWizardStep
	tenantId: string
	slug: string
	onFollow: (event: MouseEvent<HTMLAnchorElement>, href: string) => void
}) {
	if (entry.kind === WizardStepKind.EMBEDDED) {
		const Section = entry.component
		return (
			<Section
				tenantId={tenantId}
				tenantSlug={slug}
				plane={TenantConfigPlane.TENANT}
				section={entry.section}
				readOnly={false}
			/>
		)
	}
	return (
		<ul className="flex flex-col gap-2">
			{entry.links(slug).map((link) => (
				<li key={link.href}>
					<Link to={link.href} onClick={(event) => onFollow(event, link.href)} className="text-ds-primary underline">
						{link.label}
					</Link>
				</li>
			))}
		</ul>
	)
}
